/**
 * Color Helper Functions
 * 
 * Utility functions for color manipulation, contrast calculation,
 * and user color assignment for calendar events.
 */

import type { CalendarUser } from '../types/calendar';

/**
 * Convert hex color to RGB values
 */
export const hexToRgb = (hex: string): { r: number; g: number; b: number } | null => {
  // Remove # if present
  const cleanHex = hex.replace('#', '');
  
  // Parse hex to RGB
  const bigint = parseInt(cleanHex, 16);
  const r = (bigint >> 16) & 255;
  const g = (bigint >> 8) & 255;
  const b = bigint & 255;
  
  return { r, g, b };
};

/**
 * Calculate relative luminance of a color
 * Uses the formula: 0.299 * R + 0.587 * G + 0.114 * B
 */
export const getRelativeLuminance = (hex: string): number => {
  const rgb = hexToRgb(hex);
  if (!rgb) return 128; // Default to mid-range
  
  return 0.299 * rgb.r + 0.587 * rgb.g + 0.114 * rgb.b;
};

/**
 * Get contrasting text color (white or black) based on background color
 * Returns 'white' for dark backgrounds, 'black' for light backgrounds
 */
export const getContrastTextColor = (hexColor: string): string => {
  const luminance = getRelativeLuminance(hexColor);
  
  // If luminance > 128, background is light, use black text
  // If luminance <= 128, background is dark, use white text
  return luminance > 128 ? '#000000' : '#ffffff';
};

/**
 * Darken a hex color by a percentage
 * Used for creating border colors
 */
export const darkenColor = (hex: string, percent: number = 20): string => {
  const rgb = hexToRgb(hex);
  if (!rgb) return hex;
  
  const factor = 1 - (percent / 100);
  const r = Math.max(0, Math.floor(rgb.r * factor));
  const g = Math.max(0, Math.floor(rgb.g * factor));
  const b = Math.max(0, Math.floor(rgb.b * factor));
  
  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
};

/**
 * Get border color (darker shade) for a given background color
 */
export const getBorderColor = (hexColor: string): string => {
  return darkenColor(hexColor, 25);
};

/**
 * Get event colors based on delivery user's assigned color
 * Returns background, text, and border colors
 * Falls back to gray if no user or no color assigned
 */
export const getUserEventColor = (
  deliveryUser?: CalendarUser | null
): { bg: string; text: string; border: string } => {
  // Default gray color for users without assigned colors
  const defaultGray = '#6b7280'; // gray-500
  
  // Check if user has an assigned color
  const userColor = deliveryUser?.assigned_color?.hex_value;
  
  if (!userColor) {
    // No color assigned, use gray
    return {
      bg: defaultGray,
      text: getContrastTextColor(defaultGray),
      border: getBorderColor(defaultGray),
    };
  }
  
  // Use user's assigned color
  return {
    bg: userColor,
    text: getContrastTextColor(userColor),
    border: getBorderColor(userColor),
  };
};

/**
 * Validate hex color format
 */
export const isValidHexColor = (hex: string): boolean => {
  return /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/.test(hex);
};

/**
 * Get color preview style for color picker
 */
export const getColorPreviewStyle = (hexColor: string): React.CSSProperties => {
  return {
    backgroundColor: hexColor,
    width: '20px',
    height: '20px',
    borderRadius: '50%',
    display: 'inline-block',
    marginRight: '8px',
    border: '2px solid #e5e7eb',
  };
};

